import { DataTable, TruncCell } from "@/components/common/DataTable";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Plus, Trash2, RefreshCw, Play, ScanText } from "lucide-react";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import {
  type DecodingRules, type DisambigRule, type FieldAlias, type DateDecoder,
  applyBrandDisambiguation, decodeProductionDate,
} from "@/lib/brandDecoding";
import { useCompanyApplianceCategories } from "@/hooks/useCompanyApplianceCategories";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

// ----------------- Hooks -----------------

function useDecodingData() {
  return useQuery({
    queryKey: ["decoding-admin"],
    queryFn: async () => {
      const [d, a, dec] = await Promise.all([
        supabase.from("brand_decoding_rules").select("*").order("brand").order("priority", { ascending: false }),
        supabase.from("brand_field_aliases").select("*").order("brand").order("alias"),
        supabase.from("brand_date_decoders").select("*").order("brand"),
      ]);
      if (d.error) throw d.error; if (a.error) throw a.error; if (dec.error) throw dec.error;
      return { disambig: d.data ?? [], aliases: a.data ?? [], decoders: dec.data ?? [] };
    },
  });
}

async function bumpVersion() {
  await supabase.from("platform_settings")
    .update({ setting_value: { version: Math.floor(Date.now() / 1000) } })
    .eq("setting_key", "decoding_rules_version");
}

// ----------------- Page -----------------

const DECODER_KINDS = [
  { value: "bsh_fd", label: "BSH FD code" },
  { value: "electrolux_sno", label: "Electrolux S.No" },
  { value: "samsung_sn", label: "Samsung S/N (pos 8-9)" },
  { value: "whirlpool_sn", label: "Whirlpool/Indesit/Hotpoint S.N." },
  { value: "lg_sn", label: "LG S/N" },
  { value: "beko_serial", label: "Beko serial" },
  { value: "position", label: "Position-based (custom)" },
  { value: "regex", label: "Regex with named groups" },
  { value: "manual", label: "Manual lookup (note only)" },
  { value: "custom", label: "Custom (JSON config)" },
];

const APPLIES_TO = [
  { value: "model", label: "Model number" },
  { value: "product", label: "Product number" },
  { value: "serial", label: "Serial number" },
];

const CANONICAL_FIELDS = ["modelNumber", "productNumber", "serialNumber", "fdCode", "serviceNr", "dateCode", "type"];

export default function BrandDecoding() {
  const { t } = useTranslation("admin");
  const { isSuperadmin: isAdmin, isLoading } = useIsSuperadmin();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading: loadingData } = useDecodingData();
  const [tester, setTester] = useState<string>(`{
  "brand": "Siemens",
  "modelNumber": "WAW28570",
  "fdCode": "9701",
  "serialNumber": ""
}`);
  const [testerOut, setTesterOut] = useState<any>(null);

  const refresh = useMutation({
    mutationFn: async () => { await bumpVersion(); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decoding-admin"] });
      toast({ title: "Rules reloaded", description: "OCR edge function will pick up the new rules within 30 seconds." });
    },
  });

  const rules: DecodingRules = useMemo(() => ({
    disambig: (data?.disambig ?? []).filter((r: any) => r.active),
    aliases: (data?.aliases ?? []).filter((r: any) => r.active),
    decoders: (data?.decoders ?? []).filter((r: any) => r.active),
  }), [data]);

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  // Access enforced by the route guard in App.tsx.

  const runTest = () => {
    try {
      const parsed = JSON.parse(tester);
      const afterDisambig = applyBrandDisambiguation({ ...parsed }, rules);
      const date = decodeProductionDate(afterDisambig, rules);
      setTesterOut({ resolved: afterDisambig, ...date });
    } catch (e: any) {
      setTesterOut({ error: e.message });
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-4">
        <SettingsBackLink to="/platform/brands" label={t("nav.brands", { ns: "settings" })} />
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ScanText className="w-5 h-5 text-primary" />
              <h1 className="text-2xl font-bold">{t("decoding.title")}</h1>
            </div>
            <p className="text-muted-foreground text-sm">Manage how the OCR scanner identifies brands and decodes production dates.</p>
          </div>
          <Button onClick={() => refresh.mutate()} disabled={refresh.isPending} variant="outline">
            <RefreshCw className={`w-4 h-4 ${refresh.isPending ? "animate-spin" : ""}`} />
            Reload rules
          </Button>
        </div>
      </div>


      <div className="px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="disambig">
            <TabsList>
              <TabsTrigger value="disambig">Disambiguation rules</TabsTrigger>
              <TabsTrigger value="aliases">Field aliases</TabsTrigger>
              <TabsTrigger value="decoders">Date decoders</TabsTrigger>
            </TabsList>

            <TabsContent value="disambig" className="mt-4">
              <DisambigTab rows={data?.disambig ?? []} loading={loadingData} />
            </TabsContent>
            <TabsContent value="aliases" className="mt-4">
              <AliasesTab rows={data?.aliases ?? []} loading={loadingData} />
            </TabsContent>
            <TabsContent value="decoders" className="mt-4">
              <DecodersTab rows={data?.decoders ?? []} loading={loadingData} />
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-1">
          <Card className="p-4 sticky top-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2"><Play className="w-4 h-4" /> Tester</h3>
            <p className="text-xs text-muted-foreground mb-2">Paste OCR JSON and preview what the scanner would output.</p>
            <Textarea value={tester} onChange={e => setTester(e.target.value)} className="font-mono text-xs h-40" />
            <Button className="w-full mt-2" onClick={runTest}>Run</Button>
            {testerOut && (
              <div className="mt-3 space-y-2 text-sm">
                {testerOut.error ? (
                  <div className="text-destructive">{testerOut.error}</div>
                ) : (
                  <>
                    <div><span className="text-muted-foreground">Resolved brand:</span> <Badge>{testerOut.resolved.brand}</Badge></div>
                    <div><span className="text-muted-foreground">Production date:</span> {testerOut.productionDate ?? "—"}</div>
                    <div><span className="text-muted-foreground">Date code:</span> {testerOut.dateCode ?? "—"}</div>
                    {testerOut.dateNote && <div className="text-xs italic text-muted-foreground">{testerOut.dateNote}</div>}
                  </>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ----------------- Disambiguation tab -----------------

function DisambigTab({ rows, loading }: { rows: any[]; loading: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<DisambigRule> & { id?: string } | null>(null);

  const { names: applianceCategoryNames } = useCompanyApplianceCategories();

  const save = useMutation({
    mutationFn: async (row: any) => {
      // Validate regex
      try { new RegExp(row.pattern); } catch (e: any) { throw new Error(`Invalid regex: ${e.message}`); }
      const payload = {
        brand: row.brand?.trim(),
        appliance_type: row.appliance_type?.trim() || null,
        pattern: row.pattern?.trim(),
        applies_to: row.applies_to ?? "model",
        priority: Number(row.priority ?? 100),
        notes: row.notes?.trim() || null,
        active: row.active ?? true,
      };
      if (!payload.brand || !payload.pattern) throw new Error("Brand and pattern required");
      if (row.id) {
        const { error } = await supabase.from("brand_decoding_rules").update(payload).eq("id", row.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("brand_decoding_rules").insert(payload);
        if (error) throw error;
      }
      await bumpVersion();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decoding-admin"] });
      setEditing(null);
      toast({ title: "Saved" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("brand_decoding_rules").delete().eq("id", id);
      if (error) throw error;
      await bumpVersion();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decoding-admin"] }),
  });

  const toggle = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("brand_decoding_rules").update({ active }).eq("id", id);
      if (error) throw error;
      await bumpVersion();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decoding-admin"] }),
  });

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">If a model number matches the regex, override the brand.</p>
        <Button size="sm" onClick={() => setEditing({ applies_to: "model", priority: 100, active: true })}>
          <Plus className="w-4 h-4" /> Add rule
        </Button>
      </div>
      {loading ? <div>Loading…</div> : (
        <DataTable<any> storageKey="decoding-disambig-column-widths" rows={rows} rowKey={(r) => r.id}
          columns={[
            { key: "brand", header: "Brand", width: 140, sortValue: (r) => r.brand, cell: (r) => <TruncCell className="font-medium">{r.brand}</TruncCell> },
            { key: "type", header: "Appliance Type", width: 150, sortValue: (r) => r.appliance_type, cell: (r) => <TruncCell>{r.appliance_type ?? "—"}</TruncCell> },
            { key: "pattern", header: "Pattern", width: 260, sortValue: (r) => r.pattern, cell: (r) => <TruncCell className="font-mono text-xs">{r.pattern}</TruncCell> },
            { key: "applies_to", header: "Applies to", width: 120, sortValue: (r) => r.applies_to, cell: (r) => <TruncCell>{r.applies_to}</TruncCell> },
            { key: "priority", header: "Priority", width: 90, align: "right", sortValue: (r) => r.priority, cell: (r) => <span className="tabular-nums">{r.priority}</span> },
            { key: "active", header: "Active", width: 80, sortValue: (r) => (r.active ? 0 : 1), cell: (r) => <span onClick={(e) => e.stopPropagation()}><Switch checked={r.active} onCheckedChange={(v) => toggle.mutate({ id: r.id, active: v })} /></span> },
          ]}
          actionsWidth={110}
          actions={(r) => (<>
            <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Delete" onClick={() => remove.mutate(r.id)}><Trash2 className="w-4 h-4" /></Button>
          </>)}
        />
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit rule" : "New rule"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Brand</Label><Input value={editing.brand ?? ""} onChange={e => setEditing({ ...editing, brand: e.target.value })} /></div>
              <div>
                <Label>Appliance Type</Label>
                <SearchableSelect
                  value={(editing as any).appliance_type ?? "__none"}
                  onChange={v => setEditing({ ...editing, appliance_type: !v || v === "__none" ? null : v } as any)}
                  placeholder="Select"
                  searchPlaceholder="Search appliance types…"
                  emptyMessage="No appliance types found"
                  items={[{ value: "__none", label: "Any" }, ...applianceCategoryNames.map(c => ({ value: c, label: c }))]}
                />
              </div>
              <div><Label>Pattern (regex)</Label><Input className="font-mono" value={editing.pattern ?? ""} onChange={e => setEditing({ ...editing, pattern: e.target.value })} placeholder="^WAW" /></div>
              <div><Label>Applies to</Label>
                <Select value={editing.applies_to ?? "model"} onValueChange={v => setEditing({ ...editing, applies_to: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{APPLIES_TO.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority (higher = checked first)</Label><Input type="number" value={editing.priority ?? 100} onChange={e => setEditing({ ...editing, priority: Number(e.target.value) })} /></div>
              <div><Label>Notes</Label><Input value={(editing as any).notes ?? ""} onChange={e => setEditing({ ...editing, notes: e.target.value } as any)} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => save.mutate(editing)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ----------------- Aliases tab -----------------

function AliasesTab({ rows, loading }: { rows: any[]; loading: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<FieldAlias> & { id?: string } | null>(null);

  const save = useMutation({
    mutationFn: async (row: any) => {
      const payload = {
        brand: row.brand?.trim(),
        alias: row.alias?.trim(),
        canonical_field: row.canonical_field?.trim(),
        active: row.active ?? true,
      };
      if (!payload.brand || !payload.alias || !payload.canonical_field) throw new Error("All fields required");
      if (row.id) {
        const { error } = await supabase.from("brand_field_aliases").update(payload).eq("id", row.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("brand_field_aliases").insert(payload);
        if (error) throw error;
      }
      await bumpVersion();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["decoding-admin"] }); setEditing(null); toast({ title: "Saved" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("brand_field_aliases").delete().eq("id", id);
      if (error) throw error;
      await bumpVersion();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decoding-admin"] }),
  });

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">Maps label terms (E-Nr, PNC, Mat.-Nr.) to the canonical JSON field for each brand. Used to build the OCR system prompt.</p>
        <Button size="sm" onClick={() => setEditing({ active: true })}><Plus className="w-4 h-4" /> Add alias</Button>
      </div>
      {loading ? <div>Loading…</div> : (
        <DataTable<any> storageKey="decoding-aliases-column-widths" rows={rows} rowKey={(r) => r.id}
          columns={[
            { key: "brand", header: "Brand", width: 160, sortValue: (r) => r.brand, cell: (r) => <TruncCell className="font-medium">{r.brand}</TruncCell> },
            { key: "alias", header: "Alias", width: 220, sortValue: (r) => r.alias, cell: (r) => <TruncCell className="font-mono text-xs">{r.alias}</TruncCell> },
            { key: "canonical", header: "Canonical field", width: 220, sortValue: (r) => r.canonical_field, cell: (r) => <TruncCell className="font-mono text-xs">{r.canonical_field}</TruncCell> },
          ]}
          actionsWidth={110}
          actions={(r) => (<>
            <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Delete" onClick={() => remove.mutate(r.id)}><Trash2 className="w-4 h-4" /></Button>
          </>)}
        />
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit alias" : "New alias"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Brand</Label><Input value={editing.brand ?? ""} onChange={e => setEditing({ ...editing, brand: e.target.value })} /></div>
              <div><Label>Alias (label term)</Label><Input className="font-mono" value={editing.alias ?? ""} onChange={e => setEditing({ ...editing, alias: e.target.value })} placeholder="E-Nr" /></div>
              <div><Label>Canonical field</Label>
                <SearchableSelect
                  value={editing.canonical_field || null}
                  onChange={v => setEditing({ ...editing, canonical_field: v ?? "" })}
                  placeholder="Pick field"
                  searchPlaceholder="Search fields…"
                  emptyMessage="No fields found"
                  items={CANONICAL_FIELDS.map(f => ({ value: f, label: f }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => save.mutate(editing)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ----------------- Decoders tab -----------------

function DecodersTab({ rows, loading }: { rows: any[]; loading: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<DateDecoder> & { id?: string; configText?: string } | null>(null);

  useEffect(() => {
    if (editing && editing.configText === undefined) {
      setEditing({ ...editing, configText: JSON.stringify(editing.config ?? {}, null, 2) });
    }
  }, [editing]);

  const save = useMutation({
    mutationFn: async (row: any) => {
      let config: any = {};
      try { config = row.configText ? JSON.parse(row.configText) : {}; }
      catch (e: any) { throw new Error(`Invalid JSON config: ${e.message}`); }
      const payload = {
        brand: row.brand?.trim(),
        kind: row.kind,
        source_field: row.source_field?.trim() || null,
        date_code_template: row.date_code_template?.trim() || null,
        date_note: row.date_note?.trim() || null,
        config,
        active: row.active ?? true,
      };
      if (!payload.brand || !payload.kind) throw new Error("Brand and decoder kind required");
      if (row.id) {
        const { error } = await supabase.from("brand_date_decoders").update(payload).eq("id", row.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("brand_date_decoders").insert(payload);
        if (error) throw error;
      }
      await bumpVersion();
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["decoding-admin"] }); setEditing(null); toast({ title: "Saved" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("brand_date_decoders").delete().eq("id", id);
      if (error) throw error;
      await bumpVersion();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["decoding-admin"] }),
  });

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">One decoder per brand. Built-in kinds need no config; "position", "regex", and "custom" use the JSON config field.</p>
        <Button size="sm" onClick={() => setEditing({ kind: "manual", active: true, configText: "{}" })}><Plus className="w-4 h-4" /> Add decoder</Button>
      </div>
      {loading ? <div>Loading…</div> : (
        <DataTable<any> storageKey="decoding-decoders-column-widths" rows={rows} rowKey={(r) => r.id}
          columns={[
            { key: "brand", header: "Brand", width: 160, sortValue: (r) => r.brand, cell: (r) => <TruncCell className="font-medium">{r.brand}</TruncCell> },
            { key: "kind", header: "Kind", width: 130, sortValue: (r) => r.kind, cell: (r) => <Badge variant="secondary">{r.kind}</Badge> },
            { key: "source", header: "Source field", width: 180, sortValue: (r) => r.source_field, cell: (r) => <TruncCell className="font-mono text-xs">{r.source_field ?? "—"}</TruncCell> },
            { key: "template", header: "Template", width: 240, sortValue: (r) => r.date_code_template, cell: (r) => <TruncCell className="font-mono text-xs">{r.date_code_template ?? "—"}</TruncCell> },
          ]}
          actionsWidth={110}
          actions={(r) => (<>
            <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r, configText: JSON.stringify(r.config ?? {}, null, 2) })}>Edit</Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Delete" onClick={() => remove.mutate(r.id)}><Trash2 className="w-4 h-4" /></Button>
          </>)}
        />
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit decoder" : "New decoder"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Brand</Label><Input value={editing.brand ?? ""} onChange={e => setEditing({ ...editing, brand: e.target.value })} /></div>
                <div><Label>Kind</Label>
                  <Select value={editing.kind} onValueChange={v => setEditing({ ...editing, kind: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DECODER_KINDS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Source field (canonical name from extracted data)</Label><Input className="font-mono" placeholder="serialNumber" value={editing.source_field ?? ""} onChange={e => setEditing({ ...editing, source_field: e.target.value })} /></div>
              <div><Label>Date code template (use {`{fieldName}`} placeholders)</Label><Input className="font-mono" placeholder="FD {fdCode}" value={editing.date_code_template ?? ""} onChange={e => setEditing({ ...editing, date_code_template: e.target.value })} /></div>
              <div><Label>Date note (shown when date can't be auto-decoded)</Label><Textarea value={editing.date_note ?? ""} onChange={e => setEditing({ ...editing, date_note: e.target.value })} /></div>
              <div><Label>Config (JSON) — used by "position", "regex", and "custom" kinds</Label>
                <Textarea className="font-mono text-xs h-32" value={editing.configText ?? "{}"} onChange={e => setEditing({ ...editing, configText: e.target.value })}
                  placeholder='{"year_pos": 7, "month_pos": 8, "year_base": 2010}' />
                <p className="text-xs text-muted-foreground mt-1">
                  • <code>position</code>: <code>{`{"year_pos":7,"month_pos":8,"year_base":2010}`}</code><br/>
                  • <code>regex</code>: <code>{`{"pattern":"^(?<year>\\d{2})(?<month>\\d{2})","year_base":2000}`}</code>
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => save.mutate(editing)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
